import React from "react";
import i18n from "../i18n";

// Chunk loads fail for two reasons:
// 1. Flaky network / blocked request: the file exists, so retrying the import fixes it.
// 2. Stale tab after a deploy: the old hashed chunk is gone (404), so only a full
//    reload (which fetches the new index.html + hashes) fixes it.
// We retry first, then auto-reload (capped via sessionStorage so an offline user
// doesn't loop forever). Once the cap is hit, the notice offers a manual reload button.

export const RELOAD_KEY = "ff_chunk_reload";
export const MAX_AUTO_RELOADS = 3;
const RELOAD_RESET_MS = 5 * 60 * 1000;
const RETRY_DELAYS_MS = [500, 1000];
export const NOTICE_ID = "ff-refresh-notice";

export const isChunkLoadError = (error) => {
  if (!error) return false;
  if (error.name === "ChunkLoadError") return true;
  return /Loading (CSS )?chunk [\w~-]+ failed/i.test(error.message || "");
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Plain DOM so it renders regardless of React state (we are usually stuck in a Suspense fallback).
// Shown when auto-reload gave up so the user can manually reload and never sees a blank page.
export const showRefreshNotice = () => {
  try {
    const existing = document.getElementById(NOTICE_ID);
    if (existing) existing.remove();

    const notice = document.createElement("div");
    notice.id = NOTICE_ID;
    notice.setAttribute("role", "status");
    notice.setAttribute("aria-live", "polite");
    notice.style.cssText = [
      "position:fixed", "top:30vh", "left:50%", "transform:translateX(-50%)", "z-index:2147483647",
      "width:calc(100% - 32px)", "max-width:380px", "box-sizing:border-box", "padding:14px 16px",
      "background:#fff", "color:#1f2933", "border-left:4px solid #e03a3e", "border-radius:8px",
      "box-shadow:0 8px 24px rgba(0,0,0,0.18)", "font-family:inherit", "font-size:14px", "line-height:1.4",
    ].join(";");

    const title = document.createElement("strong");
    title.style.cssText = "display:block;font-size:15px;margin-bottom:2px";
    title.textContent = `🏒 ${i18n.t("chunkError.refreshTitle")}`;

    const body = document.createElement("span");
    body.style.display = "block";
    body.textContent = i18n.t("chunkError.refreshBody");

    const button = document.createElement("button");
    button.type = "button";
    button.textContent = i18n.t("chunkError.reload");
    button.style.cssText = [
      "display:block", "margin-top:10px", "padding:8px 16px", "border:0", "border-radius:4px",
      "background:#e03a3e", "color:#fff", "font:inherit", "font-weight:600", "cursor:pointer",
    ].join(";");
    button.addEventListener("click", () => window.location.reload());

    notice.appendChild(title);
    notice.appendChild(body);
    notice.appendChild(button);

    document.body.appendChild(notice);
  } catch (_) {
    // notice is cosmetic; never block
  }
};

const readReloadCount = () => {
  const state = JSON.parse(window.sessionStorage.getItem(RELOAD_KEY) || "null");
  if (!state || Date.now() - state.at > RELOAD_RESET_MS) return 0;
  return Number(state.count) || 0;
};

// Returns true when a reload was triggered. Never reloads if storage is unavailable,
// since we could not guard against a loop.
export const autoReload = () => {
  try {
    const count = readReloadCount();
    if (count >= MAX_AUTO_RELOADS) return false;
    window.sessionStorage.setItem(RELOAD_KEY, JSON.stringify({ count: count + 1, at: Date.now() }));
  } catch (_) {
    return false;
  }
  window.location.reload();
  return true;
};

export const importWithRetry = async (factory, retryDelays = RETRY_DELAYS_MS) => {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await factory();
    } catch (error) {
      if (!isChunkLoadError(error)) throw error;
      if (attempt < retryDelays.length) {
        await wait(retryDelays[attempt]);
        continue;
      }
      // Keep the Suspense fallback on screen while the page reloads.
      if (autoReload()) return new Promise(() => {});
      throw error;
    }
  }
};

const lazyWithRetry = (factory) => React.lazy(() => importWithRetry(factory));

export default lazyWithRetry;
